import { useLayoutEffect, useRef, useState } from 'react';
import { mapPlainOffsetToRaw } from './mapPlainOffsetToRaw';

export interface UseEditableMarkdownOptions {
  /** The raw markdown string being edited. */
  value: string;
  /** Called when the user edits the text in the textarea. */
  onChange: (value: string) => void;
}

export interface UseEditableMarkdownReturn {
  isEditing: boolean;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Props to spread on the textarea element. */
  textareaProps: {
    ref: React.RefObject<HTMLTextAreaElement | null>;
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onBlur: () => void;
    onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  };
  /** Call from the preview container's onClick handler. */
  onPreviewClick: (e: React.MouseEvent<HTMLElement>) => void;
  /** Call from the preview container's onKeyDown handler. */
  onPreviewKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}

/**
 * Manages the markdown-preview ↔ textarea toggle with:
 * - Click-to-edit with cursor positioned where the user clicked
 * - Blur-to-close
 * - Scroll position preservation across the DOM swap
 * - Auto-sizing textarea height
 */
export function useEditableMarkdown({
  value,
  onChange,
}: UseEditableMarkdownOptions): UseEditableMarkdownReturn {
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const cursorOffsetRef = useRef<number | null>(null);
  const savedScrollPositions = useRef<Map<Element, number>>(new Map());

  // --- Scroll preservation ---

  const saveScrollPositions = (from: HTMLElement) => {
    const positions = new Map<Element, number>();
    let el: HTMLElement | null = from;
    while (el) {
      if (el.scrollTop > 0) positions.set(el, el.scrollTop);
      el = el.parentElement;
    }
    savedScrollPositions.current = positions;
  };

  const restoreScrollPositions = () => {
    for (const [element, scrollTop] of savedScrollPositions.current) {
      element.scrollTop = scrollTop;
    }
  };

  // --- Textarea sizing + cursor placement + scroll restore ---

  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (textarea && isEditing) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.max(100, textarea.scrollHeight)}px`;

      const offset = cursorOffsetRef.current;
      if (offset !== null) {
        const pos = Math.min(offset, value.length);
        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(pos, pos);
        cursorOffsetRef.current = null;
      } else {
        textarea.focus({ preventScroll: true });
      }

      restoreScrollPositions();

      // Fallback: restore again after paint in case something resets scroll
      const positions = new Map(savedScrollPositions.current);
      if (positions.size > 0) {
        requestAnimationFrame(() => {
          for (const [element, scrollTop] of positions) {
            element.scrollTop = scrollTop;
          }
        });
      }
      savedScrollPositions.current.clear();
    }
  }, [value, isEditing]);

  // --- Event handlers ---

  const onPreviewClick = (e: React.MouseEvent<HTMLElement>) => {
    saveScrollPositions(e.currentTarget);
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const preRange = document.createRange();
      preRange.selectNodeContents(e.currentTarget);
      preRange.setEnd(range.startContainer, range.startOffset);
      const plainOffset = preRange.toString().length;
      cursorOffsetRef.current = mapPlainOffsetToRaw(value, plainOffset);
    }
    setIsEditing(true);
  };

  const onPreviewKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      saveScrollPositions(e.currentTarget);
      setIsEditing(true);
    }
  };

  return {
    isEditing,
    textareaRef,
    textareaProps: {
      ref: textareaRef,
      value,
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
        onChange(e.target.value),
      onBlur: () => setIsEditing(false),
      onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape' || (e.key === 'Enter' && e.metaKey)) {
          e.preventDefault();
          textareaRef.current?.blur();
        }
      },
    },
    onPreviewClick,
    onPreviewKeyDown,
  };
}
